// Integration Types
export interface Customer {
  id: string;
  name: string;
  psa_customer_id?: string;
  settings_json?: any;
  created_at?: string;
  updated_at?: string;
  msp_id?: number;
  is_msp_org?: boolean;
  msp_identity?: {
    auto_mode_notify_enabled?: boolean;
    default_notify_upn?: string;
    default_from_mailbox?: string;
    teams_chat_id?: string;
    teams_team_id?: string;
    teams_channel_id?: string;
  };
}

/** Numeric levels; matches Horizon `automation_config` and cortex.proto `AutomationCapacity`. */
/**
 * Legacy capacities 2 and 4 are reserved in cortex.proto, so they are not part of this union;
 * stored rows still carrying one normalize to 0 (see `normalizeAutomationCapacity`).
 */
export type AutomationCapacity = 0 | 1 | 3;

/** Numeric levels; matches Horizon `automation_config` and cortex.proto `CustomerInteraction`. */
export type CustomerInteraction = 0 | 1 | 2;

// The retired names are deliberately absent: an unknown key normalizes to 0.
const LEGACY_AUTOMATION_LABEL: Record<string, AutomationCapacity> = {
  no_automation: 0,
  suggestions: 1,
  auto_resolver: 3,
};

const LEGACY_INTERACTION_LABEL: Record<string, CustomerInteraction> = {
  no_interaction: 0,
  investigative: 1,
  full_interaction: 2,
};

/**
 * Rows saved with reserved legacy capacities 2 or 4 normalize to 0 rather than being shown as
 * Auto Resolver, which executes writes.
 */
const RETIRED_AUTOMATION_CAPACITIES = new Set([2, 4]);

const isAutomationCapacity = (n: number): n is AutomationCapacity =>
  !RETIRED_AUTOMATION_CAPACITIES.has(n) && n >= 0 && n <= 3;

/** Normalize API/DB values (int or legacy string enums) for UI state. */
export function normalizeAutomationCapacity(value: unknown): AutomationCapacity {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.trunc(value);
    if (isAutomationCapacity(n)) {
      return n;
    }
  }
  const s = String(value ?? "").trim().toLowerCase();
  if (s in LEGACY_AUTOMATION_LABEL) {
    return LEGACY_AUTOMATION_LABEL[s];
  }
  const parsed = Number.parseInt(s, 10);
  if (Number.isFinite(parsed) && isAutomationCapacity(parsed)) {
    return parsed;
  }
  return 0;
}

export function normalizeCustomerInteraction(value: unknown): CustomerInteraction {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.trunc(value);
    if (n >= 0 && n <= 2) {
      return n as CustomerInteraction;
    }
  }
  const s = String(value ?? "").trim().toLowerCase();
  if (s in LEGACY_INTERACTION_LABEL) {
    return LEGACY_INTERACTION_LABEL[s];
  }
  const parsed = Number.parseInt(s, 10);
  if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 2) {
    return parsed as CustomerInteraction;
  }
  return 0;
}

export function normalizeAutomationBoolean(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value !== 0;
  }
  const s = String(value ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
}

export type AutomationLevel = "none" | "suggestions" | "auto_resolver";

export interface AutomationRuleAssignment {
  psaUserId: string;
  label: string;
  email?: string;
  resourceId?: string;
  roleId?: string;
}

export interface AutomationRuleStatus {
  psaStatusId: string;
  label: string;
}

export type AutomationRuleCriteriaType = "free_text" | "theo_taxonomy";

/** Step 1 trigger: which ticket events can fire this rule. Defaults to both for backwards compatibility. */
export type AutomationTriggerEvent = "ticket_created" | "ticket_updated";

/** Fields a ticket-update trigger can listen to. The poller compares the same set. */
export const AGENT_UPDATE_FIELDS = ["status", "priority", "assignee", "title", "description", "queue"] as const;
export type AgentUpdateField = (typeof AGENT_UPDATE_FIELDS)[number];

export function normalizeAgentUpdateFields(value: unknown): AgentUpdateField[] {
  const raw = Array.isArray(value) ? value : [];
  const normalized = raw
    .map((entry) => String(entry ?? "").trim().toLowerCase())
    .filter((entry): entry is AgentUpdateField => (AGENT_UPDATE_FIELDS as readonly string[]).includes(entry));
  const unique = Array.from(new Set(normalized));
  return unique.length > 0 ? unique : [...AGENT_UPDATE_FIELDS];
}

export const AUTOMATION_TRIGGER_EVENTS: AutomationTriggerEvent[] = [
  "ticket_created",
  "ticket_updated",
];

export function normalizeAutomationTriggerEvents(value: unknown): AutomationTriggerEvent[] {
  const raw = Array.isArray(value) ? value : [];
  const normalized = raw
    .map((entry) => String(entry ?? "").trim().toLowerCase())
    .filter(
      (entry): entry is AutomationTriggerEvent =>
        entry === "ticket_created" || entry === "ticket_updated"
    );
  const unique = Array.from(new Set(normalized));
  // Backwards compatibility: rules saved before triggers existed fire on both events.
  return unique.length > 0 ? unique : [...AUTOMATION_TRIGGER_EVENTS];
}

/** Agent triggers. A missing list is both events. An explicit empty list stays empty. */
export function normalizeAgentTriggerEvents(value: unknown): AutomationTriggerEvent[] {
  if (!Array.isArray(value)) return [...AUTOMATION_TRIGGER_EVENTS];
  const normalized = value
    .map((entry) => String(entry ?? "").trim().toLowerCase())
    .filter(
      (entry): entry is AutomationTriggerEvent =>
        entry === "ticket_created" || entry === "ticket_updated"
    );
  return Array.from(new Set(normalized));
}

export function normalizeCustomInstructions(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.slice(0, 2000);
}

export interface TheoTaxonomyCategory {
  l1: string;
  criteria: string;
  l2: string[];
}

/**
 * Minutes Theo logs on its completion time entry, per Theo L1 category and optionally per L2. `uncategorized` is for a ticket
 * with no Theo category. As the defaults (from GET /automation-config/theo-taxonomy) every key is set.
 * As an Agent's `time_entry_minutes`, only the categories its admin changed are present.
 */
export interface AgentTimeEntryMinutes {
  uncategorized: number | null;
  categories: Record<string, number>;
  /** Per-L2 overrides, keyed by L1 then L2. An L2 not listed uses its L1's minutes. */
  subcategories?: Record<string, Record<string, number>>;
}

export const TIME_ENTRY_MIN_MINUTES = 1;
export const TIME_ENTRY_MAX_MINUTES = 480;

export interface TheoTaxonomyResponse {
  categories: TheoTaxonomyCategory[];
  time_entry_defaults?: { uncategorized: number; categories: Record<string, number> };
}

export interface TheoTaxonomySelection {
  l1: string;
  l2?: string | null;
}

export interface AutomationHandlingRule {
  id: string;
  priority: number;
  ticket_type_criteria: string;
  criteria_type?: AutomationRuleCriteriaType;
  theo_l1?: string | null;
  theo_l2?: string | null;
  theo_categories?: TheoTaxonomySelection[];
  /** Step 1 — trigger criteria: which ticket events fire this rule. */
  trigger_events?: AutomationTriggerEvent[];
  /** Step 3 — custom instructions injected into the Theo run for this rule. */
  custom_instructions?: string | null;
  automation_level: AutomationLevel;
  assignment?: AutomationRuleAssignment | null;
  status?: AutomationRuleStatus | null;
}

export interface AutomationRulesConfig {
  version: number;
  rules: AutomationHandlingRule[];
  fallback: {
    automation_level: AutomationLevel;
    assignment?: AutomationRuleAssignment | null;
    status?: AutomationRuleStatus | null;
  };
}

export interface AutomationConfig {
  id?: number;
  msp_id: number;
  msp_name: string;
  customer_id: number | null;
  customer_name: string;
  config_scope?: "msp_default" | "customer_override";
  automation_capacity: AutomationCapacity;
  suggestions_enabled?: boolean | number;
  auto_resolver_enabled?: boolean | number;
  auto_resolver_ticket_type_allowlist?: string | null;
  rules_json?: AutomationRulesConfig | string | null;
  customer_interaction: CustomerInteraction;
  created_at?: string;
  updated_at?: string;
}

/**
 * An Agent: one of the named AI workers an MSP configures.
 *
 * `slug` is the immutable machine identity — runs, logs and history key on it, so a rename never
 * rewrites history. `name` is the only user-facing label. `is_default` marks the built-in Theo
 * Technician Agent, which is editable but cannot be removed.
 *
 * `automation_capacity`, `customer_interaction` and `trigger` are null only on a default agent
 * that has not been saved from /theo-automations/configure. A null capacity means Auto Resolver.
 * A null interaction means no customer interaction. A null trigger matches no ticket. The poller
 * does not read the removed MSP handling rules for this agent. Every other agent is configured
 * explicitly.
 */
/** One of the PSA's statuses, from GET /automation-config/psa/statuses. */
export interface AgentInProgressStatus {
  id: string;
  label: string;
}

export interface Agent {
  id: number;
  msp_id: number;
  slug: string;
  name: string;
  description: string | null;
  is_default: boolean;
  enabled: boolean;
  instructions: string | null;
  trigger: AgentTrigger | null;
  automation_capacity: AutomationCapacity | null;
  customer_interaction: CustomerInteraction | null;
  /** Null means the agent has no stored technician-notify settings. */
  notify: AgentNotify | null;
  /** Who may authorize this agent starting work on a ticket. Null means its runs are not gated. */
  authorization?: AgentAuthorization | null;
  /** Sensitive Microsoft 365 writes inside Auto Resolver. Missing reads as approval. */
  autonomy?: AgentAutonomy | null;
  /** PSA status set when Theo starts a ticket. Null uses the PSA's own "In Progress" label. */
  in_progress_status?: AgentInProgressStatus | null;
  /** PSA status set when Theo finishes a ticket successfully. Null leaves the stop note's status. */
  completed_status?: AgentInProgressStatus | null;
  /** PSA user for Theo's completion time entry when the ticket has no assignee. Null skips it. */
  time_entry_resource?: AgentInProgressStatus | null;
  /** Completion time entry minutes the admin changed, per Theo L1 category. Null uses every default. */
  time_entry_minutes?: AgentTimeEntryMinutes | null;
  all_customers: boolean;
  all_connected_apps: boolean;
  customer_ids: number[];
  platform_ids: number[];
  application_ids: number[];
  created_at?: string;
  updated_at?: string;
}

/** Moments a technician notification can fire for one agent. */
export const AGENT_NOTIFY_EVENTS = ["picked_up", "needs_attention", "finished"] as const;
export type AgentNotifyEvent = (typeof AGENT_NOTIFY_EVENTS)[number];
export type AgentNotifyDestination = "assignee" | "msp";

/** Who this agent notifies, and where that message goes. End-user contact is customer_interaction. */
export interface AgentNotify {
  technician_enabled: boolean;
  events: AgentNotifyEvent[];
  /** Older rows. "msp" is the channel only. "assignee" is the technician and the channel. */
  destination?: AgentNotifyDestination;
  /** Direct message to the technician assigned on the ticket. If they are not available, the message goes to the Teams channel. */
  notify_technician?: boolean;
  /** Post in the Teams channel. Both this and notify_technician send to each. Also receives the message when the technician is not available. */
  notify_channel?: boolean;
  /** Teams team for the channel this agent posts in. */
  teams_team_id?: string;
  /** Teams channel this agent posts in. Required when notify_channel is on. */
  teams_channel_id?: string;
}

export function defaultAgentNotify(): AgentNotify {
  return {
    technician_enabled: true,
    events: [...AGENT_NOTIFY_EVENTS],
    notify_technician: true,
    notify_channel: true,
    teams_team_id: "",
    teams_channel_id: "",
  };
}

/** Checkboxes for a stored row, including one saved before the checkboxes existed. */
export function notifyTargetsFromNotify(notify: AgentNotify | null | undefined): {
  notifyTechnician: boolean;
  notifyChannel: boolean;
} {
  if (notify && (typeof notify.notify_technician === "boolean" || typeof notify.notify_channel === "boolean")) {
    return {
      notifyTechnician: notify.notify_technician === true,
      notifyChannel: notify.notify_channel === true,
    };
  }
  if (notify?.destination === "msp") return { notifyTechnician: false, notifyChannel: true };
  return { notifyTechnician: true, notifyChannel: true };
}

/** `approval` waits for a technician. `auto` lets Auto Resolver run that action. */
export type M365SensitiveWritesMode = "approval" | "auto";

/**
 * One mode per sensitive action. A string is the older whole-list setting:
 * `"approval"` or `"auto"` for every action.
 */
export type M365SensitiveWriteModes = Record<string, M365SensitiveWritesMode> | M365SensitiveWritesMode;

/**
 * One agent's ticket authorization policy. Each rule lists admin, hr, technician; any one is enough.
 * The server sets `version` and raises it whenever the policy changes.
 */
export interface AgentAuthorization {
  require_approval: boolean;
  rules: {
    others_resource: readonly string[];
    admin_change: readonly string[];
    employee_lifecycle: readonly string[];
    service_impacting: readonly string[];
  };
  version?: number;
}

export interface AgentAutonomy {
  m365_sensitive_writes: M365SensitiveWriteModes;
}

/** When this agent should pick up a ticket. An empty trigger can never match. */
export interface AgentTrigger {
  /** Plain-language topic labels, matched semantically rather than as exact strings. */
  ticket_types: string[];
  /** PSA queue or category ids, matched exactly. The most specific kind of trigger. */
  queues: string[];
  /** PSA status ids. Empty includes every status. */
  statuses?: string[];
  /** PSA priority ids. Empty includes every priority. */
  priorities?: string[];
  /** PSA assignee ids. `unassigned` matches a ticket with nobody assigned. Empty includes every assignee. */
  assignees?: string[];
  rules: Array<{
    ticket_type_criteria: string;
    theo_categories: Array<{ l1: string; l2: string | null }>;
  }>;
  /** Step 1 — which ticket events can select this agent. Missing means both. */
  events?: AutomationTriggerEvent[];
  /** Fields that count as a ticket update. Missing means every field. */
  update_fields?: AgentUpdateField[];
}

/** What a create or update sends. Omitted keys are left unchanged on update. */
export interface AgentInput {
  name?: string;
  description?: string | null;
  instructions?: string | null;
  trigger?: AgentTrigger | null;
  enabled?: boolean;
  automation_capacity?: AutomationCapacity | null;
  customer_interaction?: CustomerInteraction | null;
  notify?: AgentNotify | null;
  authorization?: AgentAuthorization;
  autonomy?: AgentAutonomy | null;
  /** Only `id` is read; the server takes the label from the PSA. Null clears it. */
  in_progress_status?: { id: string } | null;
  /** Same shape as `in_progress_status`. Null clears it. */
  completed_status?: { id: string } | null;
  /** Same shape as `in_progress_status`, from GET /automation-config/psa/assignees. Null clears it. */
  time_entry_resource?: { id: string } | null;
  /** Every category's minutes; the server keeps only those that differ from the default. Null resets all. */
  time_entry_minutes?: AgentTimeEntryMinutes | null;
  all_customers?: boolean;
  all_connected_apps?: boolean;
  customer_ids?: number[];
  platform_ids?: number[];
  application_ids?: number[];
}

export interface AgentListResponse {
  rows: Agent[];
}

/** One row per ticket this agent has worked. */
export interface AgentTicketRow {
  ticket_id: string;
  ticket_title: string | null;
  /** What the agent was called at the time, which may differ from its name now. */
  agent_name: string | null;
  auto_mode: string;
  resolver_state: string;
  psa_status_category: string;
  started_at: string | null;
  completed_at: string | null;
  ticket_updated_at: string | null;
}

export interface AgentTicketsResponse {
  agent: { id: number; slug: string; name: string };
  rows: AgentTicketRow[];
}

export interface AutomationConfigListResponse {
  rows: AutomationConfig[];
  customer_configs?: AutomationConfig[];
  default_config?: AutomationConfig | null;
}

export interface AutomationVideoAvatarResponse {
  status: "unavailable" | "pending" | "processing" | "completed" | "failed";
  video_url?: string | null;
  thumbnail_url?: string | null;
  failure_message?: string | null;
  debug?: {
    generation_started: boolean;
    instruction: string;
    session_id?: string;
    video_id?: string;
  };
}

export interface AutomationRulePreviewSample {
  ticket_id: string;
  title?: string | null;
  description?: string | null;
  customer_id?: string | null;
  queue_id?: string | null;
  category_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  ticket_type: string;
  selected_mode: AutomationLevel;
  confidence?: number | null;
  reason?: string | null;
  selected_rule?: {
    id: string;
    priority?: number | null;
    ticket_type_criteria?: string;
    automation_level: AutomationLevel;
    fallback?: boolean;
  } | null;
}

export interface AutomationRulePreviewGroup {
  id: string;
  priority: number;
  ticket_type_criteria: string;
  automation_level: AutomationLevel;
  samples: AutomationRulePreviewSample[];
  matched: number;
}

export interface AutomationRulePreviewResponse {
  rule_groups: AutomationRulePreviewGroup[];
  samples: AutomationRulePreviewSample[];
  scanned: number;
  scoped: number;
  classified: number;
  matched: number;
  model: string;
  done: boolean;
  cache_hit?: boolean;
  cached_at?: string;
  message?: string;
  warning?: string;
}

/** Owner sections group runs by who acts next; "unresolved"/"completed" are the older PSA-only split. */
export type AutoAgentMonitorOwnerSection = "needs_you" | "working" | "done_open" | "closed";
export type AutoAgentMonitorSection = AutoAgentMonitorOwnerSection | "unresolved" | "completed";
export type AutoAgentMode = "auto_resolver";
export type AutoAgentMonitorSortBy =
  | "ticket"
  | "customer"
  | "auto_mode"
  | "psa_status"
  | "execution"
  | "last_updated"
  | "last_ai_response"
  | "last_activity";
export type AutoAgentMonitorSortDirection = "asc" | "desc";

export interface AutoAgentMonitorTicket {
  id: number;
  msp_id: number;
  platform_id: number;
  platform_name: string;
  ticket_id: string;
  ticket_title?: string | null;
  ticket_updated_at?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  psa_customer_id?: string | null;
  queue_id?: string | null;
  category_id?: string | null;
  ticket_type?: string | null;
  auto_mode: AutoAgentMode;
  matched_allowlist?: string | null;
  classification_confidence?: number | string | null;
  classification_reason?: string | null;
  ai_thread_id?: number | string | null;
  request_id?: string | null;
  agent_id?: number | null;
  agent_name?: string | null;
  resolver_state: "running" | "waiting" | "success" | "cancelled" | "declined";
  resolver_context?: { on?: string; kind?: string; deadline_at?: string; reason?: string; summary?: string } | null;
  current_turn_status?: string | null;
  current_turn_started_at?: string | null;
  last_activity_at?: string | null;
  execution_error?: string | null;
  last_ai_response_at?: string | null;
  psa_status_id?: string | null;
  psa_status_name?: string | null;
  psa_status_category: "unresolved" | "completed" | "unknown";
  psa_status_checked_at?: string | null;
  detected_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  status_refresh_error?: string | null;
}

export interface AutoAgentMonitorResponse {
  section: AutoAgentMonitorSection;
  rows: AutoAgentMonitorTicket[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasNextPage: boolean;
  sortBy?: AutoAgentMonitorSortBy;
  sortDirection?: AutoAgentMonitorSortDirection;
  summary: {
    unresolved_count: number;
    completed_count: number;
    section_counts?: Record<AutoAgentMonitorOwnerSection, number>;
    refreshed_at: string;
  };
}

export interface AutoAgentMonitorBreakdownCounts {
  total: number;
  unresolved: number;
  completed: number;
  unknown: number;
}

export interface AutoAgentMonitorTicketTypeBreakdown extends AutoAgentMonitorBreakdownCounts {
  ticket_type: string | null;
}

export interface AutoAgentMonitorTicketStatusBreakdown {
  psa_status_name: string | null;
  psa_status_category: "unresolved" | "completed" | "unknown" | string;
  count: number;
}

export interface AutoAgentMonitorExecutionStatusBreakdown extends AutoAgentMonitorBreakdownCounts {
  resolver_state: string | null;
  label: string;
}

export interface AutoAgentMonitorSummary {
  window: string;
  window_hours: number | null;
  totals: AutoAgentMonitorBreakdownCounts;
  by_ticket_type: AutoAgentMonitorTicketTypeBreakdown[];
  by_ticket_status: AutoAgentMonitorTicketStatusBreakdown[];
  by_execution_status: AutoAgentMonitorExecutionStatusBreakdown[];
  /** Theo's successful tool calls on these runs' threads. Null when Cortex DB couldn't be read. */
  actions?: AutoAgentMonitorActionSummary | null;
  refreshed_at: string;
}

export interface AutoAgentMonitorActionSummary {
  total: number;
  /** Same per-action heuristic as Usage Insights' "est. time saved". */
  estimated_minutes: number;
  by_category: { category: string; label: string; count: number }[];
}

export type TheoOverviewActionSource = "agents" | "ticket_chat" | "chat_assistant" | "automations";

export interface TheoOverviewActionSummary extends AutoAgentMonitorActionSummary {
  by_category: { category: string; label: string; count: number; estimated_minutes: number }[];
  /** Who ran the actions. With an agent selected, only "agents". */
  by_source: { source: TheoOverviewActionSource; label: string; count: number; estimated_minutes: number }[];
}

/** The mapping behind every hours-saved figure, served from the table the estimate uses. */
export interface TimeSavedModel {
  categories: { category: string; label: string; minutes_per_action: number; covers: string; example: string }[];
  assumptions: string[];
  hours_per_day: number;
}

/** Every ticket Theo worked: autonomously by Agents, and in technicians' ticket chat. */
export interface TheoOverview {
  window: string;
  window_hours: number | null;
  filters: { agent_id: number | null; customer_id: number | null; customer_name: string | null };
  /** Whether this viewer may open the lists that name technicians (admins and root). */
  can_list_people?: boolean;
  agent_tickets: {
    total: number;
    by_state: { resolver_state: string; count: number }[];
    /** `actions` and `estimated_minutes` are null when Cortex DB couldn't be read. */
    by_agent: { agent_id: number; name: string; total: number; actions: number | null; estimated_minutes: number | null }[];
  };
  /**
   * Work a technician started: tickets they prompted Theo on, and Chat Assistant conversations
   * with no ticket. Null when an agent is selected: a technician's own chat belongs to no agent.
   */
  assisted:
    | { tickets: number; conversations: number; technicians: number; people: TheoOverviewTechnician[] }
    | { error: true }
    | null;
  /** Null when Cortex DB couldn't be read. */
  actions: TheoOverviewActionSummary | null;
  top_ticket_types: { ticket_type: string | null; agents: number; technicians: number; total: number }[];
  refreshed_at: string;
}

/** One of the MSP's own accounts that worked with Theo in the view, with what they did. */
export interface TheoOverviewTechnician {
  email: string;
  tickets: number;
  conversations: number;
  actions: number;
  estimated_minutes: number;
  last_active_at: string | null;
}

/** The records behind one overview figure: the most recent `limit` rows; `total` counts them all. */
export interface TheoOverviewSourceList<Row> {
  window: string;
  total: number;
  limit: number;
  rows: Row[];
}

/** One Agent's run on one ticket, behind "Agents worked N tickets". */
export interface TheoOverviewAgentTicketSource {
  ticket_id: string;
  ticket_title: string | null;
  customer_name: string | null;
  agent_id: number;
  agent_name: string;
  resolver_state: string;
  ticket_type: string | null;
  detected_at: string | null;
  completed_at: string | null;
  ai_thread_id: number | null;
}

/** A ticket a technician prompted Theo on, behind "N tickets, prompting Theo in ticket chat". */
export interface TheoOverviewAssistedTicketSource {
  ticket_id: string;
  ticket_title: string | null;
  ticket_type: string | null;
  technicians: string[];
  prompts: number;
  first_prompt_at: string | null;
  last_prompt_at: string | null;
}

/** A Chat Assistant thread with no ticket, behind "N Chat Assistant conversations". */
export interface TheoOverviewConversationSource {
  ai_thread_id: number;
  thread_name: string | null;
  technicians: string[];
  prompts: number;
  first_prompt_at: string | null;
  last_prompt_at: string | null;
}

/** One tool's successful runs by one kind of actor, behind "N hours" saved. */
export interface TheoOverviewTimeSavedSource {
  source: TheoOverviewActionSource;
  source_label: string;
  tool_name: string;
  category: string;
  category_label: string;
  minutes_each: number;
  count: number;
  estimated_minutes: number;
}

export interface TheoOverviewTimeSavedSources {
  window: string;
  total: number;
  estimated_minutes: number;
  rows: TheoOverviewTimeSavedSource[];
}

export interface TheoOverviewFilterOptions {
  agents: { value: string; label: string; is_default: boolean; deleted: boolean }[];
  customers: { value: string; label: string }[];
}

export interface AutoAgentMonitorFilterOption {
  key: string;
  label: string;
  count: number;
  category?: string | null;
}

export interface AutoAgentMonitorFilterOptions {
  customers: AutoAgentMonitorFilterOption[];
  ticket_statuses: AutoAgentMonitorFilterOption[];
  ticket_types: AutoAgentMonitorFilterOption[];
  agents: Array<{ value: string; label: string }>;
  execution_statuses: Array<{ value: string; label: string }>;
}

export type AutoAgentFunnelStage =
  | "out_of_scope"
  | "classified_drop"
  | "qualified"
  | "dropped"
  | "execution_started"
  | "execution_queued"
  | "executed"
  | "execution_completed"
  | "execution_error"
  | "execution_failed"
  | "skipped_existing_state";

export interface AutoAgentFunnelEvent {
  id: number;
  cycle_id: string;
  msp_id: number;
  platform_id: number;
  platform_name: string;
  ticket_id: string;
  ticket_title?: string | null;
  observed_at?: string | null;
  ticket_event_time?: string | null;
  ticket_created_at?: string | null;
  ticket_updated_at?: string | null;
  queue_id?: string | null;
  category_id?: string | null;
  psa_customer_id?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  event_stage: string;
  funnel_stage: AutoAgentFunnelStage | string;
  decision?: string | null;
  dropoff_reason?: string | null;
  scope_result?: string | null;
  scope_reason?: string | null;
  customer_auto_enabled?: number | boolean | null;
  config_scope?: string | null;
  config_id?: number | null;
  rules_count?: number | null;
  fallback_mode?: string | null;
  selected_mode?: string | null;
  matched_rule_id?: string | null;
  matched_rule_priority?: number | null;
  ticket_type?: string | null;
  confidence?: number | string | null;
  classification_reason?: string | null;
  auto_mode?: AutoAgentMode | null;
  ai_thread_id?: number | string | null;
  request_id?: string | null;
  execution_status?: string | null;
  error_message?: string | null;
  debug_json?: Record<string, unknown> | string | null;
  theo_category?: Array<{ l1?: string | null; l2?: string | null; confidence?: string | null }> | null;
  theo_category_label?: string | null;
  matched_theo_categories?: Array<{ l1?: string | null; l2?: string | null; confidence?: string | null }> | null;
  matched_theo_category_label?: string | null;
  psa_category_id?: string | number | null;
  ticket_type_id?: string | number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AutoAgentFunnelSummary {
  window_hours: number;
  totals: {
    cycle_count: number;
    fetched_count: number;
    scoped_count: number;
    events_detected_count: number;
    qualified_count: number;
    classified_drop_count?: number;
    dropped_count: number;
    out_of_scope_count: number;
    unchanged_count: number;
    skipped_existing_state_count?: number;
    skipped_baseline_count?: number;
    skipped_outside_window_count?: number;
    executed_count?: number;
    execution_completed_count?: number;
    error_count: number;
    execution_error_count?: number;
    execution_failed_count?: number;
    event_count: number;
    last_completed_at?: string | null;
  };
  latest_cycle?: Record<string, any> | null;
  stages: Array<{ funnel_stage: string; count: number }>;
  dropoff_reasons: Array<{ reason: string; count: number }>;
  config: {
    active_scopes: Array<{
      scope_type: "all" | "queue" | "category";
      queue_id: number | string;
      scope_name?: string | null;
      is_auto: number | boolean;
      platform_id: number;
      platform_name: string;
    }>;
    inactive_scopes: Array<Record<string, any>>;
    default_config?: Record<string, any> | null;
    customer_coverage: {
      horizon_customers_with_psa_id: number;
      explicit_customer_configs: number;
      inheriting_msp_default: number;
    };
    poller_state?: Record<string, any> | null;
  };
  refreshed_at: string;
}

export interface AutoAgentFunnelEventsResponse {
  rows: AutoAgentFunnelEvent[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasNextPage: boolean;
  window_hours: number;
  stage?: string | null;
  refreshed_at: string;
}

export interface Application {
  id: string;
  name: string;
  enabled: boolean;
  available_actions: string[];
  config?: any;
}

export interface ApplicationAction {
  id: string;
  name: string;
  permission: "auto" | "approval" | "off";
  config?: any;
  created_at?: string;
  updated_at?: string;
}

export interface Platform {
  id: number;
  type: "psa" | "rmm" | "edr" | "ph" | "docs" | "marketplace";
  name: string;
  endpoint?: string;
  token_key?: string;
  config_json?: any;
  msp_id?: number;
}

export interface PlatformAction {
  id: string;
  name: string;
  description?: string;
  parameters?: any;
  platform_type: string;
  platform_name: string;
  permission?: "auto" | "approval" | "off";
  baseline_permission?: "auto" | "approval" | "off";
  is_override?: boolean;
}

export interface ApplicationStats {
  total_actions: number;
  auto_actions: number;
  approval_actions: number;
  off_actions: number;
  last_execution?: string;
}

export interface PlatformIntegrationStatus {
  platform_type: string;
  platform_name: string;
  total_actions: number;
  auto_actions: number;
  approval_actions: number;
  status: "connected" | "disconnected" | "error";
}

export interface AuditLogEntry {
  id: string;
  customer_id: string;
  application_id: string;
  action_name: string;
  executed_at: string;
  status: "success" | "failed" | "pending";
  details?: any;
}

export interface ActionPermissionUpdate {
  customerId: string;
  applicationId: string;
  actionName: string;
  permission: "auto" | "approval" | "off";
}

export interface ActionConfigUpdate {
  customerId: string;
  applicationId: string;
  actionName: string;
  config: any;
}

export interface ApplicationStateUpdate {
  customerId: string;
  applicationId: string;
  enabled: boolean;
}

export interface CustomerApplicationSettings {
  customerId: string;
  applicationId: string;
  settings: any;
}

export interface AuditLogParams {
  customerId: string;
  applicationId: string;
  actionName: string;
  startDate: string;
  endDate: string;
}

export interface Msp {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

export interface ApplicationPlaybook {
  skill_id: number | null;
  global_skill_id?: number | null;
  skill_slug: string;
  name: string;
  description: string;
  skill_text: string;
  enabled: boolean;
  sort_order?: number;
  is_global_default?: boolean;
  is_disabled?: boolean;
  updated_at?: string | null;
  customer_id?: number | null;
  ownership_scope?: "msp" | "customer";
  theo_categories?: TheoTaxonomySelection[];
}

export interface PlaybookWritePayload {
  name: string;
  description?: string;
  skill_text?: string;
  skill_slug?: string;
  enabled?: boolean;
  sort_order?: number;
  theo_categories?: TheoTaxonomySelection[];
}

export interface ApplicationPlaybookEntry {
  entity_type: "general" | "application" | "platform";
  reference_id: number;
  name: string;
  customer_name: string | null;
  customer_id?: number | null;
  ownership_scope?: "msp" | "customer";
  platform_type: string | null;
  action_names: string[];
  skills: ApplicationPlaybook[];
}

export type ApplicationSkill = ApplicationPlaybook;
export type ApplicationSkillEntry = ApplicationPlaybookEntry;
